import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import {
  Box,
  CssBaseline,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress
} from "@mui/material";

import { ArrowBack as ArrowBackIcon, Clear as ClearIcon } from "@mui/icons-material";

import AuthApi4 from "../services/authApi";
import Navbar from "../sidebar/SideNavbar";
import Footer from "../sidebar/Footer";
import Swal from 'sweetalert2';

export default function BulkModify() {
  const location = useLocation();
  const { stbId } = location.state || {};
  const [activeSection, setActiveSection] = useState('basePackChannel');
  const navigate = useNavigate();
  const [section1Items, setSection1Items] = useState([]); // Pay Channels
  const [section2Items, setSection2Items] = useState([]); // Bouquet Packs
  const [section3Items, setSection3Items] = useState([]); // Base Pack
  const [selectedItems, setSelectedItems] = useState([]); // All selected pack IDs
  const [planD, setPlanD] = useState([]); // Table data
  const [searchTerm, setSearchTerm] = useState('');
  const [totalPrice, setTotalPrice] = useState(0);
  const [loading, setLoading] = useState(false);

  // Load initial Base Pack details
  useEffect(() => {
    addChannel('basePackChannel');
  }, []);

  const addChannel = async (section) => {
    if (section === 'bouquetChannel') bouquetChannelDetails();
    else if (section === 'basePackChannel') basePackDetails();
    else payChannelDetails();
  }

  const basePackDetails = async () => {
    let data = { records: [{ operator_id: parseInt(localStorage.getItem("ownerId")), CUST_ID: localStorage.getItem("payment_cust_num"), genre: "B", type: "bulkAddChnl" }] };
    try { const response = await AuthApi4.addChannels(data); setSection3Items(response.data.packDetails); }
    catch (e) { console.error("Base Pack API Error", e); }
  }

  const payChannelDetails = async () => {
    let data = { records: [{ operator_id: parseInt(localStorage.getItem("ownerId")), CUST_ID: localStorage.getItem("payment_cust_num"), genre: "C", type: "bulkAddChnl" }] };
    try { const response = await AuthApi4.addChannels(data); setSection1Items(response.data.packDetails); }
    catch (e) { console.error("Pay Channel API Error", e); }
  }

  const bouquetChannelDetails = async () => {
    let data = { records: [{ operator_id: parseInt(localStorage.getItem("ownerId")), CUST_ID: localStorage.getItem("payment_cust_num"), genre: "A", type: "bulkAddChnl" }] };
    try { const response = await AuthApi4.addChannels(data); setSection2Items(response.data.packDetails); }
    catch (e) { console.error("Bouquet Pack API Error", e); }
  }

  // Fetch table info and calculate total price
  const packTableInfo = async () => {
    if (selectedItems.length === 0) { setPlanD([]); setTotalPrice(0); return; }
    let data = { records: [{ operator_id: parseInt(localStorage.getItem("ownerId")), subs_id: selectedItems, type: "packInfo" }] };
    try {
      const response = await AuthApi4.addChannels(data);
      setPlanD(response.data.packDetails);
      const totalPrice1 = response.data.packDetails.reduce((acc, item) => acc + parseFloat(item.subs_grnd_tot_prc || 0), 0);
      setTotalPrice(totalPrice1);
    } catch (e) { console.error("Pack Table Info Error", e); }
  }

  // Checkbox selection
  const handleCheckboxChange = useCallback((itemId) => {
    setSelectedItems(prev => prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]);
  }, []);
  const handleBaseCheckboxChange = useCallback((itemId) => { setSelectedItems(prev => prev.includes(itemId) ? [] : [itemId]); }, []);

  useEffect(() => { packTableInfo(); }, [selectedItems]);

  const handleBackClick = () => navigate("/bulkRenewal");

  const handleSectionClick = (section) => { setActiveSection(section); addChannel(section); };

  // Search function triggered while typing
  const handleSearchChange = async (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    let data = {
      records: [{
        operator_id: parseInt(localStorage.getItem("ownerId")),
        stbId: stbId,
        genre: activeSection === 'bouquetChannel' ? 'A' : activeSection === 'basePackChannel' ? 'B' : 'C',
        subs_name: value,
        type: "packFilter"
      }],
    };
    try {
      const response = await AuthApi4.addChannels(data);
      if (activeSection === 'bouquetChannel') setSection2Items(response.data.packDetails);
      else if (activeSection === 'basePackChannel') setSection3Items(response.data.packDetails);
      else setSection1Items(response.data.packDetails);
    } catch (e) { console.error("Search API Error", e); }
  }

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
    addChannel(activeSection); // reload current section
  }

  const handleAddChannelBtn = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "Do you want to Modify Channels?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      customClass: { popup: 'small-swal-popup' }
    });
    if (!result.isConfirmed) return;
    if (selectedItems.length === 0) { Swal.fire({ icon: 'error', title: 'Packs Not Selected', text: 'Please select a pack before proceeding.' }); return; }

    setLoading(true);
    try {
      let walletData = { records: [{ CUST_ID: localStorage.getItem("payment_cust_num"), type: "wallet" }] };
      const walletRes = await AuthApi4.stbinfo(walletData);
      const billTotal = parseFloat(walletRes.data.stbdetails[0].bill_total);
      const requiredTotal = parseFloat(totalPrice) * parseFloat(stbId.length);
      if (billTotal < requiredTotal) { setLoading(false); Swal.fire({ icon: 'error', title: 'Insufficient Amount', text: 'Insufficient Amount..' }); return; }

      let data = { operator_id: parseInt(localStorage.getItem("ownerId")), CUST_ID: localStorage.getItem("payment_cust_num"), stb_ids: stbId, subs_ids: selectedItems.map(id => ({ subs_id: id })), type: "modifyChnls" };
      const response = await AuthApi4.bulkSTBRenew(data);
      setLoading(false);

      if (response.data[0].p_out_mssg_flg === 'S') Swal.fire({ icon: 'success', title: 'Modified Successfully' }).then(() => navigate("/bulkRenewal"));
      else Swal.fire({ icon: 'error', title: 'Failed to Modify Channel', text: response.data.p_out_mssg || 'Error occurred.' });

    } catch (e) { setLoading(false); console.error("Bulk Modify API Error", e); }
  }

  const handleCancelBtn = () => window.location.reload();

  return (
    <div style={{ display: "flex", background: "#F5F5F5" }}>
      <CssBaseline />
      <Navbar />
      <main style={{ flexGrow: 1, padding: '5px', minHeight: '100vh' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 10 }}>
          <Typography variant="h5">Bulk Modify & Renew</Typography>
          <IconButton onClick={handleBackClick} sx={{ marginRight: 2, backgroundColor: '#ff8c00', fontSize: '0.875rem', color: '#fff', '&:hover': { backgroundColor: '#ffad42' } }}>
            <ArrowBackIcon sx={{ fontSize: '1rem', marginRight: '4px' }} />Back
          </IconButton>
        </Box>

        <Container sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', minHeight: 'calc(100vh - 64px)', margin: 'auto', maxWidth: 'lg', height: '100vh' }}>
          <Paper sx={{ width: '100%', overflow: 'hidden', padding: 2 }}>
            
            {/* Sections */}
            <Grid container spacing={2}>
              {['basePackChannel', 'bouquetChannel', 'payChannel'].map((section, index) => {
                const sectionName = section === 'basePackChannel' ? 'Base Pack Channels' : section === 'bouquetChannel' ? 'Bouquet Packs' : 'Pay Channels';
                return (
                  <Grid item xs={4} key={index}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: activeSection === section ? '#ff8c00' : '#fff',
                        color: activeSection === section ? '#fff' : '#000',
                        '&:hover': { backgroundColor: activeSection === section ? '#ff8c00' : '#f5f5f5' },
                        height: '60%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      onClick={() => handleSectionClick(section)}
                    >
                      <CardContent>
                        <Typography variant="h6" sx={{ fontSize: '1rem' }}>{sectionName}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )
              })}
            </Grid>

            {/* Search with clear icon */}
            <Box sx={{ marginTop: 2, width: "500px", display: 'flex' }}>
              <TextField
                fullWidth
                label="Search by Pack Name"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={handleSearchChange} // search triggered while typing
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {searchTerm && (
                        <IconButton size="small" onClick={handleClearSearch}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      )}
                    </InputAdornment>
                  )
                }}
              />
            </Box>

            {/* Packs & Table */}
            <Box sx={{ marginTop: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ height: '200px', border: '1px solid black', overflowY: 'auto', paddingRight: '8px' }}>
                    <FormControl component="fieldset">
                      <FormGroup>
                        {(activeSection === 'payChannel' ? section1Items : activeSection === 'bouquetChannel' ? section2Items : section3Items)?.length > 0 ? (
                          (activeSection === 'payChannel' ? section1Items : activeSection === 'bouquetChannel' ? section2Items : section3Items).map((item, index) => (
                            <FormControlLabel
                              key={index}
                              control={
                                <Checkbox
                                  checked={selectedItems.includes(item.subs_id)}
                                  onChange={() => activeSection === 'basePackChannel' ? handleBaseCheckboxChange(item.subs_id) : handleCheckboxChange(item.subs_id)}
                                />
                              }
                              label={item.sub_name}
                            />
                          ))
                        ) : (
                          <Typography>No Packs available</Typography>
                        )}
                      </FormGroup>
                    </FormControl>
                  </Box>
                </Grid>

                <Grid item xs={6}>
                  <TableContainer component={Paper} sx={{ minWidth: 300, height: '200px', border: '1px solid black', overflowY: 'auto' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: '#ff8c00' }}>SI No</TableCell>
                          <TableCell sx={{ color: '#ff8c00' }}>Prod Id</TableCell>
                          <TableCell sx={{ color: '#ff8c00' }}>Pack Name</TableCell>
                          <TableCell sx={{ color: '#ff8c00' }}>Price</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {planD.length > 0 ? planD.map((row, index) => (
                          <TableRow key={index}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>{row.prod_id}</TableCell>
                            <TableCell>{row.subs_desc}</TableCell>
                            <TableCell>{row.subs_grnd_tot_prc}</TableCell>
                          </TableRow>
                        )) : (
                          <TableRow>
                            <TableCell colSpan={4} align="center">No data available</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </Box>

            {/* Total Price & Buttons */}
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 0',
                borderTop: '1px solid #ddd',
                marginTop: 2
              }}
            >
              {/* Total Price on the left */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <h5 style={{ margin: 0 }}>Total Price:</h5>
                <Typography variant="h6" sx={{ marginLeft: 1 }}>
                  ₹{totalPrice.toFixed(2)}
                </Typography>
              </Box>

              {/* Buttons on the right */}
              <Box>
                <Button
                  variant="outlined"
                  sx={{
                    marginRight: 2,
                    color: '#ff8c00',
                    borderColor: '#ff8c00',
                    '&:hover': { backgroundColor: '#ff8c00', borderColor: '#ffad42', color: '#fff' },
                    minWidth: '100px'
                  }}
                  onClick={handleCancelBtn}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: '#ff8c00',
                    color: '#fff',
                    '&:hover': { backgroundColor: '#ffad42' },
                    position: 'relative',
                    minWidth: '100px'
                  }}
                  onClick={handleAddChannelBtn}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress
                      size={24}
                      sx={{
                        color: '#fff',
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        marginTop: '-12px',
                        marginLeft: '-12px'
                      }}
                    />
                  ) : (
                    'Submit'
                  )}
                </Button>
              </Box>
            </Box>

          </Paper>
        </Container>
        <Footer />
      </main>
    </div>
  );
}
